import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const RestTimerContext = createContext(null);

export function useRestTimer() {
  const ctx = useContext(RestTimerContext);
  if (!ctx) throw new Error('useRestTimer must be used within RestTimerProvider');
  return ctx;
}

export function RestTimerProvider({ children }) {
  const [restTimer, setRestTimer] = useState(0);
  const [restTimerRunning, setRestTimerRunning] = useState(false);
  const [restTimerTarget, setRestTimerTarget] = useState(90);
  const [restExerciseName, setRestExerciseName] = useState(null);
  const beepEnabledRef = useRef(false);

  const startRestTimer = useCallback((seconds, exerciseName, options = {}) => {
    setRestTimer(seconds || 90);
    setRestTimerTarget(seconds || 90);
    setRestTimerRunning(true);
    setRestExerciseName(exerciseName || null);
    beepEnabledRef.current = !!options.beepEnabled;
  }, []);

  const skipRestTimer = useCallback(() => {
    setRestTimer(0);
    setRestTimerRunning(false);
    setRestExerciseName(null);
  }, []);

  const adjustRestTimer = useCallback((delta) => {
    setRestTimerTarget(prev => Math.max(10, prev + delta));
    setRestTimerRunning(running => {
      if (running) {
        setRestTimer(prev => Math.max(1, prev + delta));
      }
      return running;
    });
  }, []);

  // Countdown interval
  useEffect(() => {
    if (!restTimerRunning || restTimer <= 0) return;
    const interval = setInterval(() => {
      setRestTimer(prev => {
        if (prev <= 1) {
          setRestTimerRunning(false);
          setRestExerciseName(null);
          // Beep
          if (beepEnabledRef.current) {
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain); gain.connect(ctx.destination);
              osc.frequency.value = 880; gain.gain.value = 0.3;
              osc.start(); osc.stop(ctx.currentTime + 0.3);
              setTimeout(() => {
                const osc2 = ctx.createOscillator();
                const gain2 = ctx.createGain();
                osc2.connect(gain2); gain2.connect(ctx.destination);
                osc2.frequency.value = 1100; gain2.gain.value = 0.3;
                osc2.start(); osc2.stop(ctx.currentTime + 0.4);
              }, 350);
            } catch {}
          }
          toast.success('Rest complete!');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [restTimerRunning, restTimer]);

  return (
    <RestTimerContext.Provider value={{
      restTimer, restTimerRunning, restTimerTarget, restExerciseName,
      startRestTimer, skipRestTimer, adjustRestTimer,
    }}>
      {children}
    </RestTimerContext.Provider>
  );
}
